import { AppDispatch } from "index";
import {
  emailBuilderCloseBlockSettings,
  emailBuilderCloseInlineContent,
  emailBuilderFlushAutosave,
  emailBuilderSyncEditorDocument,
} from "redux/actions/emailBuilder/emailBuilder";
import type { TEmailBuilderAutoSaveStatus } from "redux/reducers/emailBuilder/emailBuilder";
import { endHistoryInteraction } from "./domain/document-lifecycle";
import { findBlock } from "./domain/find";
import { editorThunks } from "./domain/redux/thunks";
import {
  editorReduxStore,
  getEditorStoreState,
  runEditorThunk,
} from "./domain/store";
import { normalizeTextHtml } from "./domain/sanitize";
import { emailBuilderLabelCommitUpdate } from "./editing/richText/emailBuilderRichTextHtml";
import { readEmailBuilderRichTextStoredHtml } from "./editing/richText/emailBuilderRichTextSession";
import { decideEmailBuilderServerDraftGate } from "./emailBuilderExitLifecycle";
import { releaseEmailBuilderSettingsAutosaveDeferral } from "./emailBuilderSettingsTextAutosaveDeferral";

export type TEmailBuilderCommitAndFlushResult = "ok" | "ignored" | "failed";

function emailBuilderCommitEditorInlineEdits(): void {
  const { textEditingBlock, document } = getEditorStoreState();
  if (textEditingBlock === null) {
    endHistoryInteraction();
    return;
  }

  const { rowId, columnId, blockId } = textEditingBlock;
  const block = findBlock(document, rowId, columnId, blockId);

  if (block?.type === "button" || block?.type === "eyebrow") {
    const nextLabel = readEmailBuilderRichTextStoredHtml(blockId);
    const labelUpdate = nextLabel === null
      ? null
      : emailBuilderLabelCommitUpdate(block.text, block.fontWeight, nextLabel);
    if (labelUpdate !== null) {
      runEditorThunk(
        editorThunks.updateBlock(rowId, columnId, blockId, labelUpdate),
      );
    }
  } else if (block?.type === "buttonRow") {
    const slotIndex = textEditingBlock.slotIndex;
    if (slotIndex !== undefined) {
      const slot = block.buttons[slotIndex];
      const nextLabel = readEmailBuilderRichTextStoredHtml(blockId);
      const labelUpdate = slot === undefined || nextLabel === null
        ? null
        : emailBuilderLabelCommitUpdate(slot.label, slot.fontWeight, nextLabel);
      if (slot !== undefined && labelUpdate !== null) {
        const buttons = [...block.buttons] as typeof block.buttons;
        buttons[slotIndex] = {
          ...slot,
          label: labelUpdate.text,
          fontWeight: labelUpdate.fontWeight,
        };
        runEditorThunk(
          editorThunks.updateBlock(rowId, columnId, blockId, { buttons }),
        );
      }
    }
  } else if (block?.type === "text") {
    const storedHtml = readEmailBuilderRichTextStoredHtml(blockId);
    if (storedHtml !== null) {
      const html = normalizeTextHtml(storedHtml);
      if (html !== block.html) {
        runEditorThunk(
          editorThunks.updateBlock(rowId, columnId, blockId, { html }),
        );
      }
    }
  }

  runEditorThunk(editorThunks.setTextEditingBlock(null));
  endHistoryInteraction();
}

export function emailBuilderSyncEditorDocumentToAdmin(dispatch: AppDispatch): void {
  const editorDocument = editorReduxStore.getState().editor.document;
  dispatch(emailBuilderSyncEditorDocument(editorDocument));
}

export function emailBuilderCommitActiveEdits(dispatch: AppDispatch): void {
  emailBuilderCommitEditorInlineEdits();

  const active = window.document.activeElement;
  if (active instanceof HTMLElement) {
    active.blur();
  }

  releaseEmailBuilderSettingsAutosaveDeferral();

  dispatch(emailBuilderCloseBlockSettings());
  dispatch(emailBuilderCloseInlineContent());
  emailBuilderSyncEditorDocumentToAdmin(dispatch);
}

export async function emailBuilderCommitAndFlushDraft(params: {
  dispatch: AppDispatch;
  getAutoSaveStatus: () => TEmailBuilderAutoSaveStatus;
  getAutoSaveConflict?: () => boolean;
  isFlushing: boolean;
  setFlushing: (flushing: boolean) => void;
}): Promise<TEmailBuilderCommitAndFlushResult> {
  emailBuilderCommitActiveEdits(params.dispatch);

  const decision = decideEmailBuilderServerDraftGate({
    autoSaveStatus: params.getAutoSaveStatus(),
    isFlushing: params.isFlushing,
    autoSaveConflict: params.getAutoSaveConflict?.() ?? false,
  });

  if (decision.kind === "ignore-duplicate") {
    return "ignored";
  }

  if (decision.kind === "proceed") {
    return "ok";
  }

  params.setFlushing(true);
  const saved = await emailBuilderFlushAutosave()(params.dispatch);
  params.setFlushing(false);
  return saved ? "ok" : "failed";
}
